import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { ChartBar, Download, Package, Plus } from "@/components/icons";
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Skeleton,
  toast,
} from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { ImportOrderListQueryDTO } from "@/modules/inventory/domain/import-order-contracts";
import { OrdersListPagination } from "@/modules/orders/presentation/ui/OrdersListPagination";
import type { ImportOrderStatus } from "@/server/platform/db/schema/inventory";
import { importOrdersClient } from "../../api/import-orders-client";
import {
  useImportOrder,
  useImportOrders,
  useReceiveImportOrder,
} from "../../api/import-orders-queries";
import {
  IMPORT_ORDER_STATUS_FILTERS,
  RECEIVABLE_IMPORT_STATUSES,
} from "../../lib/import-order-labels";
import { ReceiveImportOrderDialog } from "../../ui/ReceiveImportOrderDialog";
import { ImportOrderListCard } from "../../ui/ImportOrderListCard";

const DEFAULT_LIMIT = 20;

export function ImportOrdersListPage() {
  const nav = useNavigate();
  const [statusFilter, setStatusFilter] = useState<
    ImportOrderStatus | "all" | "overdue"
  >("all");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [exporting, setExporting] = useState(false);
  const [receiveOrderId, setReceiveOrderId] = useState<string | null>(null);

  useEffect(() => {
    setOffset(0);
  }, [statusFilter, search]);

  const query = useMemo((): ImportOrderListQueryDTO => {
    const base = {
      search: search.trim() || undefined,
      limit,
      offset,
    };
    if (statusFilter === "overdue") {
      return { ...base, overdueOnly: true };
    }
    return {
      ...base,
      status: statusFilter === "all" ? undefined : statusFilter,
    };
  }, [statusFilter, search, limit, offset]);

  const exportQuery = useMemo((): ImportOrderListQueryDTO => {
    if (statusFilter === "overdue") {
      return { overdueOnly: true, search: search.trim() || undefined };
    }
    return {
      status: statusFilter === "all" ? undefined : statusFilter,
      search: search.trim() || undefined,
    };
  }, [statusFilter, search]);

  const imports = useImportOrders(query);
  const receiveDetail = useImportOrder(receiveOrderId ?? "");
  const receiveOrder = useReceiveImportOrder();
  const canWrite = useActionPermission("import_orders:write");
  const canReceive = useActionPermission("import_orders:receive");
  const rows = imports.data?.data ?? [];
  const totalCount = imports.data?.meta?.total ?? 0;

  async function exportCsv() {
    setExporting(true);
    try {
      const blob = await importOrdersClient.exportCsv(exportQuery);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `import-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error("ສົ່ງອອກ CSV ບໍ່ສຳເລັດ");
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ສັ່ງນຳເຂົ້າ
            </h2>
            <p className="text-muted-foreground text-sm">
              Taobao · 1688 · Pinduoduo — ສ້າງໃບຮັບເຂົ້າສະຕັອກຈາກໃບສັ່ງ
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <div className="flex flex-wrap gap-2">
            {canWrite ? (
              <Button
                className="rounded-full"
                onClick={() => nav({ to: "/app/import-orders/new" })}
              >
                <Plus data-icon="inline-start" />
                ສັ່ງໃໝ່
              </Button>
            ) : null}
            <Button
              variant="outline"
              className="rounded-full"
              disabled={exporting}
              onClick={() => void exportCsv()}
            >
              <Download data-icon="inline-start" />
              {exporting ? "ກຳລັງສົ່ງອອກ..." : "CSV"}
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() =>
                nav({ to: "/app/import-orders/reports/in-transit" })
              }
            >
              <ChartBar data-icon="inline-start" />
              ລາຍງານ
            </Button>
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-wrap gap-2">
            {IMPORT_ORDER_STATUS_FILTERS.map((f) => (
              <Button
                key={f.value}
                type="button"
                size="sm"
                variant={statusFilter === f.value ? "default" : "outline"}
                className="rounded-full"
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <Input
            placeholder="ຄົ້ນຫາເລກ order..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
        </div>

        {imports.isLoading ? (
          <div className="flex flex-col gap-3">
            {["a", "b", "c"].map((id) => (
              <Skeleton key={id} className="h-44 rounded-xl" />
            ))}
          </div>
        ) : totalCount === 0 ? (
          <div className="brand-card p-8">
            <Empty>
              <EmptyMedia>
                <Package />
              </EmptyMedia>
              <EmptyHeader>
                <EmptyTitle>ຍັງບໍ່ມີໃບສັ່ງນຳເຂົ້າ</EmptyTitle>
                <EmptyDescription>
                  ບັນທຶກຫຼັງສັ່ງໃນແອບ — ຕິດຕາມ landed cost ຈົນຮັບເຂົ້າສະຕັອກ
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              {rows.map((row) => {
                const canReceiveRow =
                  canReceive &&
                  RECEIVABLE_IMPORT_STATUSES.has(row.status) &&
                  row.totalRemaining > 0;

                return (
                  <ImportOrderListCard
                    key={row.id}
                    row={row}
                    canReceive={canReceiveRow}
                    onOpen={() =>
                      nav({
                        to: "/app/import-orders/$id",
                        params: { id: row.id },
                      })
                    }
                    onReceive={() => setReceiveOrderId(row.id)}
                  />
                );
              })}
            </div>
            <OrdersListPagination
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              isLoading={imports.isFetching}
              onChange={(nextOffset, nextLimit) => {
                setOffset(nextOffset);
                setLimit(nextLimit);
              }}
            />
          </div>
        )}
      </Main>

      {receiveDetail.data ? (
        <ReceiveImportOrderDialog
          open={receiveOrderId !== null}
          onOpenChange={(open) => {
            if (!open) setReceiveOrderId(null);
          }}
          order={receiveDetail.data}
          isSubmitting={receiveOrder.isPending}
          onConfirm={async (input) => {
            if (!receiveOrderId) return;
            try {
              const result = await receiveOrder.mutateAsync({
                id: receiveOrderId,
                body: input,
              });
              toast.success("ສ້າງໃບຮັບເຂົ້າແລ້ວ — ກະລຸນາຢືນຢັນເພື່ອເພີ່ມສະຕັອກ");
              setReceiveOrderId(null);
              if (result.goodsReceipt?.id) {
                nav({
                  to: "/app/inventory/receive/$id",
                  params: { id: result.goodsReceipt.id },
                });
              }
            } catch (e) {
              toast.error("ຮັບເຂົ້າລົ້ມເຫຼວ", {
                description: e instanceof Error ? e.message : undefined,
              });
            }
          }}
        />
      ) : null}
    </>
  );
}
